import { logger } from "@/utils/logger";

/**
 * PostHog Analytics — Full conversion funnel tracking
 * 
 * Funnel stages:
 * 1. LANDING — page views, CTA clicks, demo interactions
 * 2. SIGNUP — form views, attempts, success/failure
 * 3. ACTIVATION — onboarding steps, first feature use
 * 4. TRIAL — feature usage, engagement metrics
 * 5. CONVERSION — checkout initiated, subscription started
 */

let posthogWarned = false;

const getPostHog = (): any => {
  if (typeof window !== 'undefined' && (window as any).posthog) {
    return (window as any).posthog;
  }
  if (import.meta.env.DEV && !posthogWarned) {
    posthogWarned = true;
    logger.warn('[Analytics] PostHog is not initialized — all tracking calls are no-ops. Add your PostHog API key to enable analytics.');
  }
  return null;
};

export const Analytics = {
  // ─── Identity ───────────────────────────────────────────

  identify(userId: string, traits?: Record<string, any>) {
    try {
      getPostHog()?.identify(userId, traits);
    } catch { /* silent */ }
  },

  reset() {
    try {
      getPostHog()?.reset();
    } catch { /* silent */ }
  },

  setUserProperties(properties: Record<string, any>) {
    try {
      getPostHog()?.people?.set(properties);
    } catch { /* silent */ }
  },

  // ─── Generic ────────────────────────────────────────────

  track(event: string, properties?: Record<string, any>) {
    try {
      getPostHog()?.capture(event, properties);
      if (import.meta.env.DEV) {
        logger.debug(`[Analytics] ${event}`, properties);
      }
    } catch { /* silent */ }
  },

  // ─── 1. LANDING FUNNEL ─────────────────────────────────

  /** User views the landing page */
  trackLandingView(source?: string) {
    this.track('landing_page_viewed', { source });
  },

  /** User clicks a CTA button on the landing page */
  trackCTAClick(ctaLocation: string, ctaText: string) {
    this.track('cta_clicked', { cta_location: ctaLocation, cta_text: ctaText });
  },

  /** User interacts with the hero demo chat */
  trackDemoChat(messageCount: number) {
    this.track('demo_chat_used', { message_count: messageCount });
  },

  /** User views the pricing section */
  trackPricingViewed(source: string) {
    this.track('pricing_viewed', { source });
  },

  /** User views a blog post (SEO entry) */
  trackBlogPostViewed(caseId: number, title: string) {
    this.track('blog_post_viewed', { case_id: caseId, title });
  },

  /** User views a public calculator (SEO entry) */
  trackCalculatorViewed(slug: string) {
    this.track('calculator_viewed', { calculator_slug: slug });
  },

  // ─── 2. SIGNUP FUNNEL ──────────────────────────────────

  /** Auth page is opened */
  trackAuthPageViewed(mode: 'signup' | 'signin', source?: string) {
    this.track('auth_page_viewed', { mode, source });
  },

  /** User submits signup form */
  trackSignupAttempt(method: string = 'email') {
    this.track('signup_attempted', { method });
  },

  /** Signup succeeded */
  trackSignupSuccess(userId: string, method: string = 'email') {
    this.track('signup_completed', { method });
    this.identify(userId, { signup_method: method, signup_date: new Date().toISOString() });
  },

  /** Signup failed */
  trackSignupFailed(reason: string) {
    this.track('signup_failed', { reason });
  },

  /** Login succeeded */
  trackLoginSuccess(userId: string) {
    this.track('login_completed', {});
    this.identify(userId);
  },

  /** Login failed */
  trackLoginFailed(reason: string) {
    this.track('login_failed', { reason });
  },

  // ─── 3. ACTIVATION FUNNEL ──────────────────────────────

  /** Onboarding step completed */
  trackOnboardingStep(step: number, stepName: string, data?: Record<string, any>) {
    this.track('onboarding_step_completed', { step, step_name: stepName, ...data });
  },

  /** Full onboarding completed */
  trackOnboardingCompleted(specialty?: string) {
    this.track('onboarding_completed', { specialty });
  },

  /** User's first use of a feature */
  trackFirstFeatureUse(feature: string) {
    this.track('first_feature_use', { feature });
  },

  // ─── 4. TRIAL / ENGAGEMENT ─────────────────────────────

  /** Chat message sent */
  trackChatMessage(promptMode: string, isFirstMessage: boolean = false) {
    this.track('chat_message_sent', { prompt_mode: promptMode, is_first: isFirstMessage });
  },

  /** Flashcard reviewed */
  trackFlashcardReview(confidence: number, deckId?: string) {
    this.track('flashcard_reviewed', { confidence, deck_id: deckId });
  },

  /** Quiz completed */
  trackQuizCompleted(score: number, totalQuestions: number) {
    this.track('quiz_completed', { score, total_questions: totalQuestions, accuracy: Math.round((score / totalQuestions) * 100) });
  },

  /** Calculator used */
  trackCalculatorUsed(calculatorSlug: string) {
    this.track('calculator_used', { calculator_slug: calculatorSlug });
  },

  /** Clinical case viewed (authenticated) */
  trackClinicalCaseViewed(caseId: number) {
    this.track('clinical_case_viewed', { case_id: caseId });
  },

  /** Feature gate hit (trial expired) */
  trackFeatureGateHit(feature: string) {
    this.track('feature_gate_hit', { feature });
  },

  // ─── 5. CONVERSION FUNNEL ──────────────────────────────

  /** User clicks subscribe / upgrade */
  trackCheckoutInitiated(plan: string, isYearly: boolean, promoCode?: string) {
    this.track('checkout_initiated', { plan, billing_cycle: isYearly ? 'yearly' : 'monthly', promo_code: promoCode });
  },

  /** Subscription started (webhook confirms) */
  trackSubscriptionStarted(plan: string, amount?: number) {
    this.track('subscription_started', { plan, amount_eur: amount });
  },

  /** Trial started */
  trackTrialStarted() {
    this.track('trial_started', { started_at: new Date().toISOString() });
  },

  /** Trial expired */
  trackTrialExpired() {
    this.track('trial_expired', {});
  },

  // ─── Performance ───────────────────────────────────────

  trackPerformance(metricName: string, value: number, unit: string = 'ms') {
    if (import.meta.env.DEV) {
      logger.debug(`[Performance] ${metricName}: ${value}${unit}`);
    }
  },

  trackPagePerformance() {
    if (typeof window === 'undefined' || !('performance' in window)) return;
    window.addEventListener('load', () => {
      const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      if (nav) {
        this.track('page_performance', {
          load_time_ms: Math.round(nav.loadEventEnd - nav.fetchStart),
          dom_ready_ms: Math.round(nav.domContentLoadedEventEnd - nav.fetchStart),
          ttfb_ms: Math.round(nav.responseStart - nav.fetchStart),
        });
      }
    });
  },
};

// Auto-track page performance
if (typeof window !== 'undefined') {
  Analytics.trackPagePerformance();
}
